const DAYS_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export interface ScheduleSlot {
  start: string;
  end?: string;
}

export type ScheduleMap = Record<string, ScheduleSlot[]>;

export type ScheduleMapInput = Record<string, ScheduleSlot[] | undefined>;

function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(totalMinutes: number): string {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Shifts every time slot in the schedule by `offsetMinutes`.
 * If a slot crosses midnight after the shift, it is split into two slots
 * on the appropriate adjacent days.
 */
function shiftSchedule(schedule: ScheduleMapInput, offsetMinutes: number): ScheduleMap {
  if (!schedule || typeof schedule !== 'object') return {};

  const result: ScheduleMap = {};

  for (const [day, slots] of Object.entries(schedule)) {
    if (!Array.isArray(slots)) continue;
    const dayIndex = DAYS_ORDER.indexOf(day);
    if (dayIndex === -1) continue;

    for (const slot of slots) {
      const startTotal = timeToMinutes(slot.start) + offsetMinutes;
      const startDayShift = Math.floor(startTotal / 1440);
      const newStartDay = DAYS_ORDER[(((dayIndex + startDayShift) % 7) + 7) % 7];

      if (slot.end === undefined) {
        if (!result[newStartDay]) result[newStartDay] = [];
        result[newStartDay].push({ start: minutesToTime(startTotal) });
        continue;
      }

      const endTotal = timeToMinutes(slot.end) + offsetMinutes;

      const endDayShift = Math.floor(endTotal / 1440);

      const newEndDay = DAYS_ORDER[(((dayIndex + endDayShift) % 7) + 7) % 7];

      if (newStartDay === newEndDay) {
        if (!result[newStartDay]) result[newStartDay] = [];
        result[newStartDay].push({
          start: minutesToTime(startTotal),
          end: minutesToTime(endTotal),
        });
      } else {
        // Slot crosses midnight — split into two
        if (!result[newStartDay]) result[newStartDay] = [];
        result[newStartDay].push({
          start: minutesToTime(startTotal),
          end: '23:59',
        });
        if (!result[newEndDay]) result[newEndDay] = [];
        result[newEndDay].push({
          start: '00:00',
          end: minutesToTime(endTotal),
        });
      }
    }
  }

  return result;
}

/** Convert a UTC schedule (from backend) to local-time schedule for display. */
export function scheduleUtcToLocal(schedule: ScheduleMapInput): ScheduleMap {
  const offsetMinutes = -new Date().getTimezoneOffset(); // positive east of UTC
  return shiftSchedule(schedule, offsetMinutes);
}

/** Convert a local-time schedule to UTC for sending to the backend. */
export function scheduleLocalToUtc(schedule: ScheduleMapInput): ScheduleMap {
  const offsetMinutes = new Date().getTimezoneOffset(); // positive west of UTC
  return shiftSchedule(schedule, offsetMinutes);
}
